import { languageItemList } from 'components';
import { formatPlatformLabel } from '@brand-lolly/app/social-media-influencer/modules/social-listening/utils/analytics-label.utils';
import { allCountries } from '@brand-lolly/app/shared/storage/countries-data-store';
import {
	FindCity,
	FindFilters,
	FindRegion
} from '@brand-lolly/app/social-media-influencer/modules/social-listening/models/social-listening-find-mentions.model';

export type FindFilterField = keyof FindFilters;

/** One applied filter value, as a chip names it and as its removal changes it. */
export interface FindFilterChip {
	label: string;
	field: FindFilterField;
	value: string | number;
	/** Read off a place term of the query rather than picked by the brand. */
	inferred?: boolean;
}

/** What an inferred location chip adds to its place's name. */
export const FIND_INFERRED_LOCATION_SUFFIX = ' · from your query';

let regionNames: Intl.DisplayNames | null | undefined;

/**
 * The short English name of an ISO country code ("United Kingdom", not the
 * ISO official "United Kingdom of Great Britain and Northern Ireland" that
 * allCountries carries); falls back to that name, then to the code.
 */
export function findCountryLabel(code: string): string {
	if (regionNames === undefined) {
		try {
			regionNames = new Intl.DisplayNames(['en'], { type: 'region' });
		} catch {
			regionNames = null;
		}
	}
	let label: string | undefined;
	try {
		label = regionNames?.of(code.toUpperCase());
	} catch {
		label = undefined;
	}
	if (label && label !== code.toUpperCase()) return label;
	return (
		allCountries.find(country => country.code === code)?.name.trim() ?? code
	);
}

/** "Paris, France"; just the name when the country is unknown. */
export function findCityLabel(city: FindCity): string {
	return city.country
		? `${city.name}, ${findCountryLabel(city.country)}`
		: city.name;
}

/** "Rajasthan, India"; just the name when the country is unknown. */
export function findRegionLabel(region: FindRegion): string {
	return region.country
		? `${region.name}, ${findCountryLabel(region.country)}`
		: region.name;
}

function cityChipLabel(filters: FindFilters, id: number): string {
	const city = filters.cities?.find(item => item.id === id);
	return city ? findCityLabel(city) : `City ${id}`;
}

function regionChipLabel(filters: FindFilters, id: number): string {
	const region = filters.regions?.find(item => item.id === id);
	return region ? findRegionLabel(region) : `Region ${id}`;
}

/**
 * One chip per place term the backend inferred a location from, named by
 * its most specific level still applied. An inferred city or region carries
 * its country as a second record of the same term — that country gets no
 * chip of its own, and none while the PATCH removing the city is in flight
 * (the backend drops it then). Returns the chips and the values they cover.
 */
function inferredLocationChips(filters: FindFilters): {
	chips: FindFilterChip[];
	covered: Set<string>;
} {
	const regionIds = filters.region_ids ?? [];
	const chips: FindFilterChip[] = [];
	const covered = new Set<string>();
	const records = filters.inferred_locations ?? [];
	const terms = Array.from(new Set(records.map(record => record.term)));
	terms.forEach(term => {
		const own = records.filter(record => record.term === term);
		const narrower = own.filter(record => record.level !== 'country');
		const applied: FindFilterChip[] = [];
		narrower.forEach(record => {
			const id = Number(record.value);
			if (record.level === 'city' && filters.city_ids.includes(id)) {
				applied.push({
					label: cityChipLabel(filters, id),
					field: 'city_ids',
					value: id
				});
			} else if (record.level === 'region' && regionIds.includes(id)) {
				applied.push({
					label: regionChipLabel(filters, id),
					field: 'region_ids',
					value: id
				});
			}
		});
		const countries = own
			.filter(record => record.level === 'country')
			.map(record => String(record.value))
			.filter(code => filters.countries.includes(code));
		countries.forEach(code => covered.add(`countries:${code}`));
		if (!narrower.length) {
			countries.forEach(code =>
				applied.push({
					label: findCountryLabel(code),
					field: 'countries',
					value: code
				})
			);
		}
		applied.forEach(chip => {
			covered.add(`${chip.field}:${chip.value}`);
			chips.push({
				...chip,
				label: chip.label + FIND_INFERRED_LOCATION_SUFFIX,
				inferred: true
			});
		});
	});
	return { chips, covered };
}

function capitalised(value: string): string {
	return value.charAt(0).toUpperCase() + value.slice(1);
}

/** Every applied filter value in display order: platforms, sentiment, emotion, language, location, then the locations inferred from the query. */
export function findFilterChips(filters: FindFilters): FindFilterChip[] {
	const inferred = inferredLocationChips(filters);
	const picked = (field: FindFilterField, value: string | number) =>
		!inferred.covered.has(`${field}:${value}`);
	return [
		...filters.platforms.map(value => ({
			label: formatPlatformLabel(String(value)),
			field: 'platforms' as const,
			value
		})),
		...filters.sentiments.map(value => ({
			label: capitalised(value),
			field: 'sentiments' as const,
			value
		})),
		...filters.emotions.map(value => ({
			label: capitalised(value),
			field: 'emotions' as const,
			value
		})),
		...filters.languages.map(value => ({
			label:
				languageItemList.find(language => language.id === value)
					?.name ?? value.toUpperCase(),
			field: 'languages' as const,
			value
		})),
		...filters.countries
			.filter(value => picked('countries', value))
			.map(value => ({
				label: findCountryLabel(value),
				field: 'countries' as const,
				value
			})),
		...(filters.region_ids ?? [])
			.filter(value => picked('region_ids', value))
			.map(value => ({
				label: regionChipLabel(filters, value),
				field: 'region_ids' as const,
				value
			})),
		...filters.city_ids
			.filter(value => picked('city_ids', value))
			.map(value => ({
				label: cityChipLabel(filters, value),
				field: 'city_ids' as const,
				value
			})),
		...inferred.chips
	];
}
